import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { SeverityBadge, StatusBadge } from "@/components/Primitives";
import { Sparkles, Send, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function TicketDetail() {
  const { id } = useParams();
  const [t, setT] = useState(null);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [body, setBody] = useState("");
  const [internal, setInternal] = useState(false);
  const [summary, setSummary] = useState("");
  const [rca, setRca] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);

  const load = async () => {
    const [ticketRes, usersRes, clientsRes] = await Promise.allSettled([
      api.get(`/tickets/${id}`),
      api.get("/users"),
      api.get("/clients"),
    ]);

    if (ticketRes.status === "fulfilled") setT(ticketRes.value.data);
    if (usersRes.status === "fulfilled") setUsers(usersRes.value.data);
    if (clientsRes.status === "fulfilled") setClients(clientsRes.value.data);
  };

  useEffect(() => { load(); }, [id]);

  const userMap = useMemo(() => Object.fromEntries(users.map(u => [u.id, u])), [users]);
  const clientMap = useMemo(() => Object.fromEntries(clients.map(c => [c.id, c])), [clients]);

  const technicians = users.filter(u =>
    ["msp_owner", "service_manager", "service_desk", "technician", "admin"].includes(u.role)
  );

  const addComment = async () => {
    if (!body.trim()) return;
    await api.post(`/tickets/${id}/comments`, { body, internal });
    setBody("");
    setInternal(false);
    toast.success(internal ? "Internal note added" : "Reply added");
    load();
  };

  const change = async (field, val) => {
    await api.patch(`/tickets/${id}`, { [field]: val });
    toast.success("Updated");
    load();
  };

  const aiSummary = async () => {
    setLoadingAI(true);
    try {
      const r = await api.post(`/ai/ticket-summary/${id}`);
      setSummary(r.data.summary);
    } finally {
      setLoadingAI(false);
    }
  };

  const aiRca = async () => {
    setLoadingAI(true);
    try {
      const r = await api.post(`/ai/rca/${id}`);
      setRca(r.data);
    } finally {
      setLoadingAI(false);
    }
  };

  if (!t) return <div className="text-zinc-500 text-sm">Loading…</div>;

  return (
    <div className="space-y-4">
      <Link to="/tickets" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-blue-400">
        <ArrowLeft size={14} /> Back to tickets
      </Link>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-4">
          <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5">
            <div className="flex items-center gap-3">
              <div className="text-zinc-500 font-mono text-sm">#{t.number}</div>
              <SeverityBadge level={t.priority} />
              <StatusBadge status={t.status} />
              <span className="text-xs text-zinc-500 ml-auto font-mono">SLA: {t.sla_deadline?.slice(0,16).replace("T"," ")}</span>
            </div>

            <h1 className="font-display text-2xl font-semibold tracking-tight mt-3">{t.title}</h1>
            <p className="text-sm text-zinc-400 mt-2 whitespace-pre-wrap">{t.description}</p>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-2">
              <Field label="Status">
                <select data-testid="ticket-status-update" value={t.status} onChange={e=>change("status", e.target.value)} className="input">
                  {["open","in_progress","pending","closed"].map(s=><option key={s}>{s}</option>)}
                </select>
              </Field>

              <Field label="Priority">
                <select data-testid="ticket-priority-update" value={t.priority} onChange={e=>change("priority", e.target.value)} className="input">
                  {["critical","high","medium","low"].map(s=><option key={s}>{s}</option>)}
                </select>
              </Field>

              <Field label="Assignee">
                <select value={t.assignee_id || ""} onChange={e=>change("assignee_id", e.target.value)} className="input">
                  <option value="">Unassigned</option>
                  {technicians.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                </select>
              </Field>

              <Field label="Category">
                <select value={t.category || "general"} onChange={e=>change("category", e.target.value)} className="input">
                  {["general","hardware","software","network","printer","security","account access","backup","performance"].map(s=><option key={s}>{s}</option>)}
                </select>
              </Field>
            </div>
          </div>

          <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5">
            <div className="font-display font-medium mb-3">Conversation</div>

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {(t.comments || []).map(c => (
                <div key={c.id} className={`border rounded p-3 ${c.internal ? "border-amber-900/60 bg-amber-950/20" : "border-zinc-900 bg-zinc-900/30"}`}>
                  <div className="flex items-center gap-2 text-xs text-zinc-500">
                    <span className="text-zinc-300 font-medium">
                      {c.actor_name || c.author_name || c.author_email || "User"}
                    </span>
                    <span className="font-mono">
                      {(c.at || c.created_at)?.slice(0,16)?.replace("T"," ")}
                    </span>
                    {c.internal && <span className="text-amber-300 bg-amber-950 border border-amber-900/70 rounded px-2 py-0.5">internal</span>}
                  </div>
                  <div className="text-sm text-zinc-300 mt-2 whitespace-pre-wrap">{c.body || c.comment}</div>
                </div>
              ))}

              {(t.comments || []).length === 0 && <div className="text-zinc-500 text-sm">No comments yet.</div>}
            </div>

            <div className="mt-3 space-y-2">
              <textarea data-testid="ticket-comment-input" value={body} onChange={e=>setBody(e.target.value)} rows={3} placeholder={internal ? "Add internal note…" : "Add public reply…"} className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500/60" />

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-xs text-zinc-400">
                  <input type="checkbox" checked={internal} onChange={e=>setInternal(e.target.checked)} />
                  Internal note only
                </label>

                <button data-testid="ticket-comment-submit" onClick={addComment} className="bg-blue-600 hover:bg-blue-700 rounded px-3 py-2 text-white text-sm flex items-center gap-1">
                  <Send size={14} /> {internal ? "Add Internal Note" : "Send Reply"}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={14} className="text-blue-400" />
              <div className="font-display font-medium">AI Operations</div>
            </div>

            <div className="flex gap-2 flex-wrap mb-3">
              <button data-testid="ai-summary-btn" onClick={aiSummary} disabled={loadingAI} className="bg-blue-600 hover:bg-blue-700 text-white text-xs rounded px-3 py-1.5">Summarize</button>
              <button data-testid="ai-rca-btn" onClick={aiRca} disabled={loadingAI} className="bg-zinc-900 border border-zinc-800 text-zinc-100 text-xs rounded px-3 py-1.5">Root Cause</button>
            </div>

            {summary && <pre className="text-xs whitespace-pre-wrap text-zinc-300 leading-relaxed max-h-64 overflow-auto border border-zinc-900 rounded p-3 mb-3" data-testid="ai-summary-text">{summary}</pre>}

            {rca && (
              <div className="space-y-2">
                <pre className="text-xs whitespace-pre-wrap text-zinc-300 leading-relaxed max-h-72 overflow-auto border border-zinc-900 rounded p-3">{rca.rca}</pre>
                {rca.related_tickets?.length > 0 && (
                  <div className="text-[11px] text-zinc-500 font-mono">{rca.related_tickets.length} related historical tickets analyzed.</div>
                )}
              </div>
            )}
          </div>

          <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5 text-xs space-y-2 text-zinc-400">
            <div className="font-display text-sm text-zinc-200 mb-2">Context</div>
            <div>Client: <span className="font-mono">{clientMap[t.client_id]?.name || t.client_id || "—"}</span></div>
            <div>Assignee: <span className="font-mono">{userMap[t.assignee_id]?.name || userMap[t.assignee_id]?.email || "Unassigned"}</span></div>
            {t.asset_id && <div>Asset: <Link to={`/assets/${t.asset_id}`} className="font-mono text-blue-400 hover:underline">{t.asset_id}</Link></div>}
            <div>Created: <span className="font-mono">{t.created_at?.slice(0,16).replace("T"," ")}</span></div>
            <div>Updated: <span className="font-mono">{t.updated_at?.slice(0,16).replace("T"," ")}</span></div>
          </div>
        </div>
      </div>

      <style>{`
        .input {
          width: 100%;
          background: rgb(24 24 27);
          border: 1px solid rgb(39 39 42);
          border-radius: 0.375rem;
          padding: 0.45rem 0.65rem;
          font-size: 0.75rem;
          outline: none;
        }
        .input:focus {
          border-color: rgba(59, 130, 246, 0.7);
        }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">{label}</div>
      {children}
    </label>
  );
}
